import { useEffect, useState, useCallback } from "react";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";

export default function GuardianFailedPaymentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/guardian/admin/failed-payments", { params: { resolved: false } });
    setItems(data.items);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleResend = async (id) => {
    setResendingId(id);
    try {
      await api.post(`/guardian/admin/failed-payments/${id}/resend`);
      toast({ title: "Recovery email sent" });
      fetchItems();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not resend", description: err.response?.data?.detail });
    } finally {
      setResendingId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>;
  if (items.length === 0) return <p className="text-amtm-textMuted text-center py-12" data-testid="guardian-failed-payments-empty">No open failed payments. 🎉</p>;

  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="guardian-failed-payments-list">
      {items.map((f) => (
        <div key={f.id} className="flex items-center justify-between p-4 gap-3 flex-wrap" data-testid={`guardian-failed-payment-${f.id}`}>
          <div className="min-w-0">
            <p className="font-semibold text-amtm-text">{f.donor_name || "Unknown donor"}</p>
            <p className="text-sm text-amtm-textMuted">{f.donor_email || "No email on file"} &middot; {formatDate(f.created_at)}</p>
            {f.error_description && <p className="text-xs text-amtm-textMuted">{f.error_description}</p>}
            {f.recovery_email_sent_at && <p className="text-xs text-amtm-success" data-testid={`guardian-failed-payment-resent-${f.id}`}>Recovery email sent {formatDate(f.recovery_email_sent_at)}</p>}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={resendingId === f.id || !f.donor_email}
            onClick={() => handleResend(f.id)}
            data-testid={`guardian-resend-recovery-${f.id}`}
          >
            {resendingId === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Resend recovery email
          </Button>
        </div>
      ))}
    </div>
  );
}
